import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RiArrowRightLine, RiArrowLeftLine } from "react-icons/ri";
import PropertyComponent from "../../components/PropertyComponent";
import config from "../../config";
import { deleteFavoriteFromProperties } from "../../helpers/api";
import FilterModal from "../Properties/FilterModal";
import Header from "../Header/Header";
import Balls from "../Templates/Loading";
import PreviewPage from "../Preview/Preview";
import { getApi, postApi } from "../AppApis";
import PropertySkeleton from "../MyProperties/PropertySkeleton";

const PROPERTIES_PER_PAGE = 12;

const FavoriteProperties = () => {
    const navigate = useNavigate();

    // Core state
    const [favoriteProperties, setFavoriteProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [previewModal, setPreviewModal] = useState(null);
    const [unfavoriteLoading, setUnfavoriteLoading] = useState(new Set());
    const [isFavoriteChanged, setIsFavoriteChanged] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        minPrice: "",
        maxPrice: "",
        beds: "",
        baths: "",
        homeType: "",
        sort: "",
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sort_by, setSort_by] = useState("Newest");

    // Set document title
    useEffect(() => {
        document.title = `Favorites | ${config.get("COMPANY_NAME")}`;
    }, []);

    // Helper function to get auth user
    const getAuthUser = useCallback(() => {
        const authUserToken = localStorage.getItem("authUser");
        return authUserToken ? authUserToken.replace(/^"|"$/g, "") : "";
    }, []);

    // Load favorite properties from API
    const loadFavoriteProperties = async () => {
        setLoading(true);
        try {
            // Prepare API payload, only include non-empty filter values
            const payload = {
                page: currentPage,
                sort_by: sort_by,
            };

            // Add filters only if they have values
            if (filters.beds && filters.beds !== "") {
                payload.beds = filters.beds;
            }
            if (filters.baths && filters.baths !== "") {
                payload.baths = filters.baths;
            }
            if (filters.minPrice && filters.minPrice !== "") {
                payload.minPrice = filters.minPrice;
            }
            if (filters.maxPrice && filters.maxPrice !== "") {
                payload.maxPrice = filters.maxPrice;
            }
            if (filters.homeType && filters.homeType !== "") {
                payload.PropertySubType = filters.homeType;
            }

            const response = await postApi("/property-services/favorite_properties/search", payload);
            if (response?.response?.items) {
                const { items, pagination } = response.response;
                setFavoriteProperties(items);
                setTotalPages(pagination.total_pages || 1);

                // Update localStorage
                localStorage.setItem("propertiesData", JSON.stringify(items));
            } else {
                setFavoriteProperties([]);
                setTotalPages(1);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Error loading favorite properties:", error);
            setFavoriteProperties([]);
            setTotalPages(1);
            setCurrentPage(1);
        } finally {
            setLoading(false);
        }
    };

    // Load properties on mount, page change, and sort change
    // Note: filters is intentionally NOT in dependency array
    // Filters are only applied when user clicks "Apply" button (via applyFilters function)
    useEffect(() => {
        loadFavoriteProperties();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sort_by]);

    // Refetch properties when favorite status changes in preview
    useEffect(() => {
        if (isFavoriteChanged > 0) {
            loadFavoriteProperties();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFavoriteChanged]);

    // Function to apply filters when Apply button is clicked
    const applyFilters = useCallback(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        } else {
            // If already on page 1, reload data with new filters
            loadFavoriteProperties();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, loadFavoriteProperties]);

    // This useEffect has been removed to prevent automatic filter application
    // Filters are now only applied when the user clicks the Apply button in the FilterModal

    // Handle favorite toggle
    const handleFavorite = useCallback(
        async (property) => {
            const propertyId = property.ListingId;

            // Prevent duplicate requests
            if (unfavoriteLoading.has(propertyId)) {
                return;
            }

            const authUser = getAuthUser();

            // Set loading state for this property
            setUnfavoriteLoading((prev) => new Set(prev).add(propertyId));

            try {
                await deleteFavoriteFromProperties(authUser, propertyId);

                // Remove from state
                setFavoriteProperties((prev) => prev.filter((fav) => fav.ListingId !== propertyId));

                // Update localStorage
                const updatedFavorites = favoriteProperties.filter((fav) => fav.ListingId !== propertyId);
                localStorage.setItem("propertiesData", JSON.stringify(updatedFavorites));
                localStorage.setItem("favoriteProperties", JSON.stringify(updatedFavorites.map((fav) => fav.ListingId)));
            } catch (error) {
                console.error("Error removing favorite:", error);
            } finally {
                // Remove loading state for this property
                setUnfavoriteLoading((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(propertyId);
                    return newSet;
                });
            }
        },
        [getAuthUser, favoriteProperties, unfavoriteLoading]
    );

    // Navigation handlers
    const navigateToChatPage = useCallback(
        (property) => {
            const authUser = getAuthUser();
            if (!authUser) {
                navigate("/login");
                return;
            }
            navigate(`/chat?chat_id=${property.ListingId}&property_id=${property.ListingId}`, { state: { ...property } });
        },
        [getAuthUser, navigate]
    );

    const navigateToProperties = useCallback(
        (e) => {
            e.preventDefault();
            navigate("/properties");
        },
        [navigate]
    );

    // Pagination handlers
    const handlePreviousPage = () => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
    };

    // Filter handlers
    const toggleModal = useCallback(() => setIsModalOpen((prev) => !prev), []);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    }, []);

    const clearFilters = useCallback(() => {
        const clearedFilters = {
            minPrice: "",
            maxPrice: "",
            beds: "",
            baths: "",
            homeType: "",
            sort: "",
        };
        setFilters(clearedFilters);
        // Reset to page 1 and reload data after clearing filters
        setCurrentPage(1);
    }, []);

    // Check if any filters are currently applied
    const areFiltersApplied = useCallback(() => {
        return (
            filters.minPrice !== "" ||
            filters.maxPrice !== "" ||
            filters.beds !== "" ||
            filters.baths !== "" ||
            filters.homeType !== ""
        );
    }, [filters]);

    // Render loading state
    if (loading) {
        return (
            <div className="tw-h-screen tw-overflow-y-auto tw-bg-background_color tw-pt-[70px] tw-pb-3">
                <Header title="Favorites" />
                <div className="tw-px-5 tw-mx-auto">
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        <section className="tw-w-full tw-px-2">
                            {/* Results and filters header skeleton */}
                            <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
                                <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-24 tw-mb-4 tw-animate-pulse"></div>
                                <div className="tw-flex tw-gap-2">
                                    <div className="tw-flex tw-flex-wrap tw-justify-end tw-items-end tw-gap-2 tw-mb-4">
                                        <div className="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-w-20 tw-animate-pulse"></div>
                                        <div className="tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-w-32 tw-animate-pulse"></div>
                                    </div>
                                </div>
                            </div>

                            {/* Properties grid skeleton */}
                            <section className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-grid-cols-4 2xl:tw-grid-cols-4 tw-gap-[1.5rem] tw-pb-20">
                                {[...Array(8)].map((_, index) => (
                                    <PropertySkeleton key={index} />
                                ))}
                            </section>
                        </section>
                    </div>
                </div>
            </div>
        );
    }

    // Render empty state
    if (favoriteProperties.length === 0 && !loading) {
        const hasFiltersApplied = areFiltersApplied();

        return (
            <div className="tw-h-screen tw-overflow-y-auto tw-bg-background_color tw-pt-[70px] tw-pb-3">
                <Header title="Favorites" />
                <div className="tw-px-5 tw-mx-auto">
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        <section className="tw-w-full tw-px-2">
                            {/* Always show filters and sorting dropdowns */}
                            <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
                                <p className="resultsText tw-mb-4">0 results</p>
                                <div className="tw-flex tw-gap-2">
                                    <div className="filtersDiv tw-flex tw-flex-wrap tw-justify-end tw-items-end tw-gap-2 tw-mb-4">
                                        <button
                                            onClick={toggleModal}
                                            className="tw-select-filter-common tw-bg-white tw-border tw-border-gray-300 tw-w-[40%] tw-rounded-lg tw-text-sm tw-text-gray-700"
                                        >
                                            Filters
                                        </button>
                                        <select
                                            name="sort"
                                            value={sort_by}
                                            onChange={(e) => {
                                                setSort_by(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="tw-select-filter-common tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-text-gray-700 tw-w-[40%]"
                                        >
                                            <option value="">Sort</option>
                                            <option value="Newest">Sort: Newest</option>
                                            <option value="Oldest">Sort: Oldest</option>
                                            <option value="PriceLowToHigh">Sort: Price (Low to High)</option>
                                            <option value="PriceHighToLow">Sort: Price (High to Low)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Empty state message */}
                            <div className="tw-h-[70vh] tw-flex tw-flex-col tw-items-center tw-justify-center">
                                <p className="tw-font-roboto tw-font-normal tw-text-[20px] tw-leading-[26px] tw-flex tw-items-center tw-text-center tw-text-[#4C4C4C]">
                                    {hasFiltersApplied
                                        ? "No favorite properties found for the selected filters. Try adjusting your filters."
                                        : "No Favorites Yet 🙁 Find a new favorite in our Map Search 🗺️🙂"}
                                </p>
                                {hasFiltersApplied ? (
                                    <button
                                        className="tw-text-blue-500 tw-underline tw-ml-1 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-mt-2"
                                        onClick={clearFilters}
                                    >
                                        Clear Filters
                                    </button>
                                ) : (
                                    <a className="tw-text-blue-500 tw-underline tw-ml-1 tw-mt-2" href="" onClick={navigateToProperties}>
                                        Find Properties
                                    </a>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Filter Modal */}
                    {isModalOpen && (
                        <FilterModal
                            isOpen={isModalOpen}
                            toggle={toggleModal}
                            filters={filters}
                            setFilters={setFilters}
                            clearFilters={clearFilters}
                            getCornersProperties={applyFilters}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="tw-h-screen tw-overflow-y-auto tw-bg-background_color tw-pt-[70px] tw-pb-3">
            <Header title="Favorites" />
            <div className="tw-px-5 tw-mx-auto">
                <div className="tw-flex tw-flex-col tw-gap-3">
                    <section className="tw-w-full tw-px-2">
                        {/* Results and filters header */}
                        <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
                            <p className="resultsText tw-mb-4">
                                {favoriteProperties.length} result{favoriteProperties.length !== 1 ? "s" : ""}
                            </p>
                            <div className="tw-flex tw-gap-2">
                                <div className="filtersDiv tw-flex tw-flex-wrap tw-justify-end tw-items-end tw-gap-2 tw-mb-4">
                                    <button
                                        onClick={toggleModal}
                                        className="tw-select-filter-common tw-bg-white tw-border tw-border-gray-300 tw-w-[40%] tw-rounded-lg tw-text-sm tw-text-gray-700"
                                    >
                                        Filters
                                    </button>
                                    <select
                                        name="sort"
                                        value={sort_by}
                                        onChange={(e) => {
                                            setSort_by(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className="tw-select-filter-common tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-text-gray-700 tw-w-[40%]"
                                    >
                                        <option value="">Sort</option>
                                        <option value="Newest">Sort: Newest</option>
                                        <option value="Oldest">Sort: Oldest</option>
                                        <option value="PriceLowToHigh">Sort: Price (Low to High)</option>
                                        <option value="PriceHighToLow">Sort: Price (High to Low)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Properties grid */}
                        <section className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-grid-cols-4 2xl:tw-grid-cols-4 tw-gap-[1.5rem] tw-pb-20">
                            {favoriteProperties.map((property) => (
                                <PropertyComponent
                                    key={property.ListingId}
                                    item={property}
                                    isFavorite={true}
                                    setPreviewModal={setPreviewModal}
                                    navigateToChatPage={navigateToChatPage}
                                    handleFavorite={handleFavorite}
                                    favoriteProperties={favoriteProperties.map((item) => item.ListingId)}
                                    isUnfavoriteLoading={unfavoriteLoading.has(property.ListingId)}
                                />
                            ))}
                        </section>
                    </section>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination-controls">
                        <button onClick={handlePreviousPage} disabled={currentPage === 1} className="pagination-button">
                            <RiArrowLeftLine />
                        </button>
                        <span className="pagination-info">
                            {currentPage} of {totalPages}
                        </span>
                        <button onClick={handleNextPage} disabled={currentPage === totalPages} className="pagination-button">
                            <RiArrowRightLine />
                        </button>
                    </div>
                )}

                {/* Filter Modal */}
                {isModalOpen && (
                    <FilterModal
                        isOpen={isModalOpen}
                        toggle={toggleModal}
                        filters={filters}
                        setFilters={setFilters}
                        clearFilters={clearFilters}
                        getCornersProperties={applyFilters}
                    />
                )}
            </div>

            {/* Preview Modal */}
            {previewModal && (
                <PreviewPage
                    navigate={navigate}
                    id={previewModal}
                    setPreviewModal={setPreviewModal}
                    setIsFavoriteChanged={setIsFavoriteChanged}
                    isFavoriteChanged={isFavoriteChanged}
                />
            )}
        </div>
    );
};

export default FavoriteProperties;
